import React, { Fragment, useState } from "react";
import axios from "axios";
import { toast } from "react-toastify";
import Navbar from "../../components/Navbar/Navbar";
import Footer from "../../components/footer/Footer";
import Scrollbar from "../../components/scrollbar/scrollbar";

const useCases = [
  {
    title: "Employee Rewards",
    desc:
      "Recognise milestones, performance and tenure with gift cards your team actually wants — from Amazon and Noon to dining, travel and lifestyle brands.",
    icon: (
      <svg viewBox="0 0 24 24" width="22" height="22" fill="currentColor" aria-hidden="true">
        <path d="M19 5h-2V3H7v2H5c-1.1 0-2 .9-2 2v1c0 2.55 1.92 4.63 4.39 4.94.63 1.5 1.98 2.63 3.61 2.96V19H7v2h10v-2h-4v-3.1c1.63-.33 2.98-1.46 3.61-2.96C19.08 12.63 21 10.55 21 8V7c0-1.1-.9-2-2-2zM5 8V7h2v3.82C5.84 10.4 5 9.3 5 8zm14 0c0 1.3-.84 2.4-2 2.82V7h2v1z" />
      </svg>
    ),
  },
  {
    title: "Bulk Gift Cards",
    desc:
      "Place orders for hundreds or thousands at once. Custom denominations, consolidated invoicing, and bulk CSV delivery to your HR or procurement team.",
    icon: (
      <svg viewBox="0 0 24 24" width="22" height="22" fill="currentColor" aria-hidden="true">
        <path d="M20 6h-2.18c.11-.31.18-.65.18-1a2.996 2.996 0 0 0-5.5-1.65l-.5.67-.5-.68C10.96 2.54 10.05 2 9 2 7.34 2 6 3.34 6 5c0 .35.07.69.18 1H4c-1.11 0-1.99.89-1.99 2L2 19c0 1.11.89 2 2 2h16c1.11 0 2-.89 2-2V8c0-1.11-.89-2-2-2zM9 4c.55 0 1 .45 1 1s-.45 1-1 1-1-.45-1-1 .45-1 1-1zm6 0c.55 0 1 .45 1 1s-.45 1-1 1-1-.45-1-1 .45-1 1-1zm5 15H4v-2h16v2zm0-5H4V8h5.08L7 10.83 8.62 12 12 7.4l3.38 4.6L17 10.83 14.92 8H20v6z" />
      </svg>
    ),
  },
  {
    title: "Promotions & Campaigns",
    desc:
      "Run customer acquisition, loyalty or referral programs with instant-redemption gift codes. API-ready for marketing automation at scale.",
    icon: (
      <svg viewBox="0 0 24 24" width="22" height="22" fill="currentColor" aria-hidden="true">
        <path d="M3 9v6h4l5 5V4L7 9H3zm13.5 3a4.5 4.5 0 0 0-2.5-4.03v8.05A4.5 4.5 0 0 0 16.5 12zM14 3.23v2.06c2.89.86 5 3.54 5 6.71s-2.11 5.85-5 6.71v2.06c4.01-.91 7-4.49 7-8.77s-2.99-7.86-7-8.77z" />
      </svg>
    ),
  },
  {
    title: "Incentives & Partner Gifts",
    desc:
      "Motivate sales teams, thank channel partners, or reward survey participants with branded digital gift cards delivered in minutes.",
    icon: (
      <svg viewBox="0 0 24 24" width="22" height="22" fill="currentColor" aria-hidden="true">
        <path d="M12 2L9.19 8.63 2 9.24l5.46 4.73L5.82 21 12 17.27 18.18 21l-1.64-7.03L22 9.24l-7.19-.61L12 2z" />
      </svg>
    ),
  },
];

const benefits = [
  "Volume discounts on bulk orders",
  "Dedicated corporate account manager",
  "Consolidated monthly invoicing",
  "Bulk CSV / API-based delivery",
  "NDA & compliance documentation available",
  "Multi-currency billing (AED, USD)",
];

const stats = [
  { value: "500+", label: "Verified Brands" },
  { value: "10K+", label: "Cards Delivered" },
  { value: "<2h", label: "Reply Time" },
  { value: "24/7", label: "Account Support" },
];

const EMPTY_FORM = {
  company: "",
  name: "",
  email: "",
  phone: "",
  volume: "",
  message: "",
};

const NAME_RE = /^[A-Za-z][A-Za-z .'-]{1,}$/;
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const PHONE_RE = /^\+?[0-9 ()-]{7,20}$/;

const validateForm = (form) => {
  const errors = {};
  const company = form.company.trim();
  const name = form.name.trim();
  const email = form.email.trim();
  const phone = form.phone.trim();
  const message = form.message.trim();

  if (!company || company.length < 2) errors.company = "Please enter a valid company name (min 2 characters).";
  else if (!/[A-Za-z]/.test(company)) errors.company = "Company name must contain letters.";

  if (!NAME_RE.test(name)) errors.name = "Please enter a valid name (letters and spaces only).";
  if (!EMAIL_RE.test(email)) errors.email = "Please enter a valid work email.";
  if (phone && !PHONE_RE.test(phone)) errors.phone = "Please enter a valid phone number.";
  if (!message || message.length < 10) errors.message = "Tell us a bit more (at least 10 characters).";

  return errors;
};

const CorporateOrders = () => {
  const [form, setForm] = useState(EMPTY_FORM);
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors((prev) => {
        const next = { ...prev };
        delete next[name];
        return next;
      });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isSubmitting) return;

    const validationErrors = validateForm(form);
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      const firstError = Object.values(validationErrors)[0];
      toast.dismiss();
      toast.error(firstError);
      return;
    }

    setIsSubmitting(true);
    toast.dismiss();

    try {
      const { data } = await axios.post("corporate-form", form);
      toast.success(data?.message || "Enquiry sent. Our team will be in touch shortly.");
      setForm(EMPTY_FORM);
      setErrors({});
    } catch (error) {
      const apiMessage = error?.response?.data?.message;
      const friendly =
        error?.response?.status === 429
          ? apiMessage || "You can only submit one enquiry every 24 hours."
          : apiMessage || "Something went wrong. Please try again later.";
      toast.error(friendly);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Fragment>
      <Navbar />

      <section className="gpco-section">
        <div className="gpco-bg-orb gpco-bg-orb-tl" />
        <div className="gpco-bg-orb gpco-bg-orb-br" />

        <div className="gpco-inner">
          <div className="gpco-head">
            <div className="gpco-eyebrow">
              <span className="gpco-eyebrow-dot" />
              FOR BUSINESSES
            </div>
            <h1 className="gpco-title">
              Gift cards at <span className="gpco-title-accent">scale</span>
            </h1>
            <p className="gpco-sub">
              Built for HR, Marketing &amp; Procurement. Whether you're rewarding 10 employees
              or running a 10,000-customer campaign, our corporate program gives you volume
              pricing, dedicated support and flexible delivery.
            </p>
          </div>

          {/* STATS */}
          <div className="gpco-stats">
            {stats.map((s, i) => (
              <div className="gpco-stat" key={i}>
                <span className="gpco-stat-val">{s.value}</span>
                <span className="gpco-stat-label">{s.label}</span>
              </div>
            ))}
          </div>

          {/* USE CASES */}
          <div className="gpco-grid">
            {useCases.map((u, i) => (
              <article key={i} className="gpco-card">
                <div className="gpco-card-glow" aria-hidden="true" />
                <div className="gpco-card-icon">{u.icon}</div>
                <h3 className="gpco-card-title">{u.title}</h3>
                <p className="gpco-card-desc">{u.desc}</p>
              </article>
            ))}
          </div>

          {/* SPLIT */}
          <div className="gpco-split">
            <div className="gpco-benefits">
              <div className="gpco-mini-eyebrow">WHAT'S INCLUDED</div>
              <h3>Everything a corporate account needs</h3>
              <ul>
                {benefits.map((b, i) => (
                  <li key={i}>
                    <span className="gpco-tick">
                      <svg viewBox="0 0 24 24" width="12" height="12" fill="currentColor">
                        <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z" />
                      </svg>
                    </span>
                    <span>{b}</span>
                  </li>
                ))}
              </ul>

              <div className="gpco-contactline">
                <strong>Prefer to talk directly?</strong>
                <a href="mailto:support@globalgiftpass.io">
                  <svg viewBox="0 0 24 24" width="14" height="14" fill="currentColor" aria-hidden="true">
                    <path d="M20 4H4c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 4l-8 5-8-5V6l8 5 8-5z" />
                  </svg>
                  support@globalgiftpass.io
                </a>
                <a
                  href="https://wa.me/971545740174?text=Hello%20GlobalGiftPass%2C%20I'm%20interested%20in%20corporate%20orders."
                  target="_blank"
                  rel="noreferrer"
                  className="gpco-wa-link"
                >
                  <svg viewBox="0 0 24 24" width="14" height="14" fill="currentColor" aria-hidden="true">
                    <path d="M20.52 3.48A11.94 11.94 0 0012.06 0C5.5 0 .19 5.31.19 11.86c0 2.09.55 4.13 1.6 5.93L0 24l6.34-1.66a11.85 11.85 0 005.71 1.45h.01c6.55 0 11.86-5.31 11.86-11.86 0-3.17-1.23-6.15-3.4-8.45z" />
                  </svg>
                  WhatsApp +971 54 574 0174
                </a>
              </div>
            </div>

            <form className="gpco-form" onSubmit={handleSubmit}>
              <div className="gpco-mini-eyebrow">REQUEST A QUOTE</div>
              <h3>Tell us about your needs</h3>
              <p>Share your requirements and we'll be in touch.</p>

              <div className="gpco-field">
                <label>Company name</label>
                <input
                  required
                  name="company"
                  value={form.company}
                  onChange={handleChange}
                  placeholder="Acme FZ LLC"
                  aria-invalid={!!errors.company}
                  disabled={isSubmitting}
                />
                {errors.company && <span className="gpco-error">{errors.company}</span>}
              </div>
              <div className="gpco-row">
                <div className="gpco-field">
                  <label>Your name</label>
                  <input
                    required
                    name="name"
                    value={form.name}
                    onChange={handleChange}
                    placeholder="Full name"
                    aria-invalid={!!errors.name}
                    disabled={isSubmitting}
                  />
                  {errors.name && <span className="gpco-error">{errors.name}</span>}
                </div>
                <div className="gpco-field">
                  <label>Work email</label>
                  <input
                    required
                    type="email"
                    name="email"
                    value={form.email}
                    onChange={handleChange}
                    placeholder="you@company.com"
                    aria-invalid={!!errors.email}
                    disabled={isSubmitting}
                  />
                  {errors.email && <span className="gpco-error">{errors.email}</span>}
                </div>
              </div>
              <div className="gpco-row">
                <div className="gpco-field">
                  <label>Phone</label>
                  <input
                    name="phone"
                    value={form.phone}
                    onChange={handleChange}
                    placeholder="+971 ..."
                    aria-invalid={!!errors.phone}
                    disabled={isSubmitting}
                  />
                  {errors.phone && <span className="gpco-error">{errors.phone}</span>}
                </div>
                <div className="gpco-field">
                  <label>Estimated volume</label>
                  <select
                    name="volume"
                    value={form.volume}
                    onChange={handleChange}
                    disabled={isSubmitting}
                  >
                    <option value="">Select range</option>
                    <option>Under 50 cards</option>
                    <option>50 – 500 cards</option>
                    <option>500 – 5,000 cards</option>
                    <option>5,000+ cards</option>
                  </select>
                </div>
              </div>
              <div className="gpco-field">
                <label>Tell us more</label>
                <textarea
                  rows={4}
                  name="message"
                  value={form.message}
                  onChange={handleChange}
                  placeholder="Use case, target brands, timeline..."
                  aria-invalid={!!errors.message}
                  disabled={isSubmitting}
                />
                {errors.message && <span className="gpco-error">{errors.message}</span>}
              </div>
              <button type="submit" className="gpco-submit" disabled={isSubmitting}>
                {isSubmitting ? (
                  <span className="gpco-spinner" />
                ) : (
                  <>
                    Send Enquiry
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
                      <line x1="5" y1="12" x2="19" y2="12" />
                      <polyline points="12 5 19 12 12 19" />
                    </svg>
                  </>
                )}
              </button>
            </form>
          </div>
        </div>

        <style>{`
          .gpco-section {
            position: relative;
            background: linear-gradient(180deg, #07060a 0%, #0b0b0c 100%);
            color: #e6e8ec;
            padding: 60px 22px 90px;
            font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
            overflow: hidden;
          }
          .gpco-bg-orb {
            position: absolute;
            width: 460px;
            height: 460px;
            border-radius: 50%;
            filter: blur(140px);
            opacity: 0.4;
            pointer-events: none;
          }
          .gpco-bg-orb-tl { background: rgba(212,168,87,0.18); top: -180px; left: -120px; }
          .gpco-bg-orb-br { background: rgba(212,168,87,0.12); bottom: -200px; right: -120px; }

          .gpco-inner { position: relative; max-width: 1200px; margin: 0 auto; z-index: 1; }

          .gpco-head { text-align: center; margin-bottom: 32px; }
          .gpco-eyebrow {
            display: inline-flex; align-items: center; gap: 8px;
            padding: 6px 14px;
            background: rgba(212,168,87,0.1);
            border: 1px solid rgba(212,168,87,0.3);
            color: #d4a857;
            border-radius: 999px;
            font-size: 11px; font-weight: 700; letter-spacing: 2px;
            margin-bottom: 14px;
          }
          .gpco-eyebrow-dot { width: 6px; height: 6px; border-radius: 50%; background: #d4a857; box-shadow: 0 0 10px #d4a857; animation: gpco-pulse 1.6s ease-in-out infinite; }
          @keyframes gpco-pulse { 0%,100% { opacity:1; transform: scale(1); } 50% { opacity:.55; transform: scale(.85); } }
          .gpco-title {
            font-family: "Playfair Display", "Times New Roman", serif;
            font-size: 42px; font-weight: 700; color: #fff; margin: 0 0 12px; line-height: 1.15;
          }
          .gpco-title-accent {
            background: linear-gradient(135deg, #f1cf85, #b58a3f);
            -webkit-background-clip: text; background-clip: text; color: transparent;
            font-style: italic; font-weight: 600;
          }
          .gpco-sub { font-size: 15px; color: #9aa0a8; max-width: 720px; margin: 0 auto; line-height: 1.65; }

          /* STATS */
          .gpco-stats {
            display: grid;
            grid-template-columns: repeat(4, 1fr);
            gap: 12px;
            margin-bottom: 32px;
          }
          .gpco-stat {
            background: linear-gradient(180deg, #15110a 0%, #1a1208 100%);
            border: 1px solid rgba(212,168,87,0.2);
            border-radius: 16px;
            padding: 20px;
            text-align: center;
            display: flex; flex-direction: column; gap: 6px;
            transition: border-color .25s ease, transform .2s ease;
          }
          .gpco-stat:hover { border-color: rgba(212,168,87,0.45); transform: translateY(-2px); }
          .gpco-stat-val {
            font-family: "Playfair Display", serif;
            font-size: 28px;
            font-weight: 700;
            background: linear-gradient(135deg, #f1cf85, #b58a3f);
            -webkit-background-clip: text; background-clip: text; color: transparent;
          }
          .gpco-stat-label { font-size: 11.5px; color: #9aa0a8; text-transform: uppercase; letter-spacing: 1.4px; font-weight: 700; }

          /* USE CASES */
          .gpco-grid {
            display: grid;
            grid-template-columns: repeat(4, 1fr);
            gap: 16px;
            margin-bottom: 36px;
          }
          .gpco-card {
            position: relative;
            background: linear-gradient(180deg, #15110a 0%, #1a1208 100%);
            border: 1px solid rgba(212,168,87,0.2);
            border-radius: 18px;
            padding: 26px 22px;
            overflow: hidden;
            transition: transform .25s ease, border-color .25s ease, box-shadow .25s ease;
          }
          .gpco-card:hover {
            transform: translateY(-4px);
            border-color: rgba(212,168,87,0.55);
            box-shadow: 0 16px 36px rgba(212,168,87,0.14);
          }
          .gpco-card-glow {
            position: absolute;
            top: -60px;
            right: -60px;
            width: 160px;
            height: 160px;
            background: radial-gradient(circle, rgba(212,168,87,0.18), transparent 70%);
            opacity: 0;
            transition: opacity .3s ease;
            pointer-events: none;
          }
          .gpco-card:hover .gpco-card-glow { opacity: 1; }
          .gpco-card-icon {
            width: 46px; height: 46px;
            background: rgba(212,168,87,0.12);
            border: 1px solid rgba(212,168,87,0.28);
            color: #d4a857;
            border-radius: 12px;
            display: flex; align-items: center; justify-content: center;
            margin-bottom: 14px;
          }
          .gpco-card-title {
            font-family: "Playfair Display", serif;
            font-size: 17px; color: #fff; margin: 0 0 8px; font-weight: 700;
          }
          .gpco-card-desc { font-size: 13px; color: #9aa0a8; line-height: 1.6; margin: 0; }

          /* SPLIT */
          .gpco-split {
            display: grid;
            grid-template-columns: 1fr 1.05fr;
            gap: 20px;
            align-items: stretch;
          }
          .gpco-mini-eyebrow {
            font-size: 11px; color: #d4a857;
            text-transform: uppercase; letter-spacing: 2px;
            font-weight: 700; margin-bottom: 8px;
          }
          .gpco-benefits {
            background: linear-gradient(180deg, #15110a 0%, #1a1208 100%);
            border: 1px solid rgba(212,168,87,0.2);
            border-radius: 18px;
            padding: 32px 28px;
            display: flex; flex-direction: column;
          }
          .gpco-benefits h3 {
            font-family: "Playfair Display", serif;
            color: #fff; font-size: 22px; margin: 0 0 18px; font-weight: 700; line-height: 1.3;
          }
          .gpco-benefits ul { list-style: none; padding: 0; margin: 0 0 24px; display: flex; flex-direction: column; gap: 8px; }
          .gpco-benefits li {
            display: flex; align-items: flex-start; gap: 12px;
            padding: 10px 12px;
            background: rgba(255,255,255,0.02);
            border: 1px solid rgba(212,168,87,0.15);
            border-radius: 10px;
            color: #c5c8cf;
            font-size: 13.5px;
            line-height: 1.45;
            transition: border-color .2s ease, background .2s ease;
          }
          .gpco-benefits li:hover {
            border-color: rgba(212,168,87,0.35);
            background: rgba(212,168,87,0.06);
          }
          .gpco-tick {
            width: 20px; height: 20px;
            background: rgba(212,168,87,0.16);
            border: 1px solid rgba(212,168,87,0.32);
            color: #d4a857;
            border-radius: 50%;
            display: inline-flex; align-items: center; justify-content: center;
            flex-shrink: 0; margin-top: 1px;
          }
          .gpco-contactline {
            margin-top: auto;
            padding: 16px;
            background: rgba(212,168,87,0.06);
            border: 1px dashed rgba(212,168,87,0.3);
            border-radius: 12px;
            display: flex; flex-direction: column; gap: 8px;
            font-size: 13px;
          }
          .gpco-contactline strong {
            color: #d4a857; font-size: 11px;
            text-transform: uppercase; letter-spacing: 1.2px; font-weight: 700;
            margin-bottom: 4px;
          }
          .gpco-contactline a {
            display: inline-flex; align-items: center; gap: 8px;
            color: #fff; text-decoration: none; font-weight: 500;
            transition: color .2s ease;
          }
          .gpco-contactline a:hover { color: #d4a857; }
          .gpco-contactline a svg { color: #d4a857; flex-shrink: 0; }
          .gpco-wa-link svg { color: #25d366 !important; }

          /* FORM */
          .gpco-form {
            background: linear-gradient(180deg, #15110a 0%, #1a1208 100%);
            border: 1px solid rgba(212,168,87,0.22);
            border-radius: 18px;
            padding: 32px 28px;
          }
          .gpco-form h3 {
            font-family: "Playfair Display", serif;
            color: #fff; font-size: 22px; margin: 0 0 4px; font-weight: 700;
          }
          .gpco-form > p { color: #9aa0a8; font-size: 13.5px; margin: 0 0 20px; }
          .gpco-row { display: grid; grid-template-columns: 1fr 1fr; gap: 14px; }
          .gpco-field { margin-bottom: 14px; display: flex; flex-direction: column; gap: 6px; }
          .gpco-field label {
            font-size: 11px;
            color: #9aa0a8;
            text-transform: uppercase;
            letter-spacing: 1.2px;
            font-weight: 700;
          }
          .gpco-field input,
          .gpco-field select,
          .gpco-field textarea {
            background: rgba(0,0,0,0.35);
            border: 1px solid rgba(212,168,87,0.22);
            color: #fff;
            border-radius: 10px;
            padding: 12px 14px;
            font-size: 14px;
            font-family: inherit;
            outline: none;
            transition: border-color .2s ease, background .2s ease;
            color-scheme: dark;
          }
          .gpco-field input::placeholder, .gpco-field textarea::placeholder { color: #6e7480; }
          .gpco-field input:focus,
          .gpco-field select:focus,
          .gpco-field textarea:focus { border-color: rgba(212,168,87,0.6); background: rgba(0,0,0,0.5); }
          .gpco-field input[aria-invalid="true"],
          .gpco-field select[aria-invalid="true"],
          .gpco-field textarea[aria-invalid="true"] { border-color: rgba(255,90,90,0.5); }
          .gpco-field input:disabled, .gpco-field textarea:disabled, .gpco-field select:disabled { opacity: 0.6; cursor: not-allowed; }
          .gpco-field select option { background: #15110a; color: #fff; }
          .gpco-error {
            display: block;
            color: #ff7a7a;
            font-size: 12px;
            font-weight: 600;
          }
          .gpco-field textarea { resize: vertical; min-height: 100px; }

          .gpco-submit {
            width: 100%;
            display: inline-flex; align-items: center; justify-content: center; gap: 10px;
            background: linear-gradient(135deg, #f1cf85, #d4a857 50%, #b58a3f);
            color: #0b0b0c;
            border: none;
            padding: 14px;
            border-radius: 12px;
            font-size: 13px;
            font-weight: 800;
            letter-spacing: 0.5px;
            text-transform: uppercase;
            cursor: pointer;
            font-family: inherit;
            min-height: 50px;
            transition: transform .2s ease, box-shadow .25s ease, gap .2s ease;
          }
          .gpco-submit:hover:not(:disabled) {
            transform: translateY(-2px);
            box-shadow: 0 14px 30px rgba(212,168,87,0.35);
            gap: 12px;
          }
          .gpco-submit:disabled { opacity: 0.7; cursor: not-allowed; }
          .gpco-spinner {
            width: 18px; height: 18px;
            border: 2px solid rgba(11,11,12,0.3);
            border-top-color: #0b0b0c;
            border-radius: 50%;
            animation: gpco-spin .7s linear infinite;
          }
          @keyframes gpco-spin { to { transform: rotate(360deg); } }

          @media (max-width: 1100px) {
            .gpco-grid { grid-template-columns: repeat(2, 1fr); }
          }
          @media (max-width: 991px) {
            .gpco-stats { grid-template-columns: repeat(2, 1fr); }
            .gpco-split { grid-template-columns: 1fr; }
            .gpco-title { font-size: 32px; }
          }
          @media (max-width: 575px) {
            .gpco-section { padding: 40px 14px 70px; }
            .gpco-title { font-size: 26px; }
            .gpco-grid { grid-template-columns: 1fr; gap: 14px; }
            .gpco-benefits, .gpco-form { padding: 24px 20px; }
            .gpco-row { grid-template-columns: 1fr; gap: 0; }
            .gpco-stat-val { font-size: 22px; }
          }
        `}</style>
      </section>

      <Footer />
      <Scrollbar />
    </Fragment>
  );
};

export default CorporateOrders;
